// Which sensor assignments a node will accept (#199).
//
// The authority on this is the backend: `sensorsError()` in routes/nodes.mjs
// rejects a repeated deviceId with 400, and it has to, because `sensors[]` is
// replaced wholesale by whoever writes it and more than one client does. A
// guard here cannot replace that one.
//
// What it is for is the part the backend cannot do: telling the operator, in
// the dialog they are looking at, before they commit. Without it the only
// feedback is a 400 that arrives after the dialog has closed and the sensor has
// already been drawn on the map, surfacing as the generic "Change was not
// saved. It is shown here only and will be gone after a refresh." banner. That
// is true, and it is useless — it names no sensor, no node, and no cause.

/**
 * Whether `deviceId` is already assigned to `node`.
 *
 * Tolerates a missing node, a missing `sensors` array and malformed entries:
 * this runs on every render of the dialog, and a node whose sensors array has a
 * bad entry in it is exactly the state #199 exists to clean up, so it must not
 * throw while the operator is trying to fix it.
 */
export function isSensorOnNode(node, deviceId) {
  if (!node || typeof deviceId !== "string" || deviceId === "") return false;
  const sensors = Array.isArray(node.sensors) ? node.sensors : [];
  return sensors.some((s) => s && s.deviceId === deviceId);
}

/**
 * The node a dialog selection refers to, or null.
 *
 * Separate from isSensorOnNode so the dialog can say *which* node it clashed
 * with by label rather than by id.
 */
export function findNode(pins, nodeId) {
  if (!Array.isArray(pins) || !nodeId) return null;
  return pins.find((p) => p && p.nodeId === nodeId) ?? null;
}

/**
 * Whether the current dialog selection would be rejected as a duplicate.
 *
 * Only the 'existing' mode can produce one: 'new' builds a node around a single
 * sensor, so there is nothing for it to repeat.
 */
export function isDuplicateSelection({ addMode, pins, nodeId, deviceId }) {
  if (addMode !== "existing") return false;
  return isSensorOnNode(findNode(pins, nodeId), deviceId);
}
